import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { ScrollableContent } from '@repo/design-system/components/layout/scrollable-content';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { getHost } from '@repo/host/host';
import { css } from '@repo/panda/css';
import { useGetGameMapQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectCurrentMapInfo, selectExternalMapId, selectIsInCreatorMode, selectMapCategories, selectMapSubcategories, setLastSelectedCategory, setLastSelectedSubcategory } from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import type { MapDataCategory, MapDataLocationFeature, MapDataPinDescriptionTemplate, MapDataSubcategory } from '@repo/types/maps/map-types';
import { createWemodProtocolUrl } from '@repo/types/maps/map-utils';
import { MarkdownEditor } from '@repo/ui/components/markdown-editor/markdown-editor';
import { copyTextToClipboard } from '@repo/utility/clipboard';
import { updateMapPinMedia } from '@repo/wemod-api-client/maps-api-client';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreatorToolOperations } from '../../game-map/hooks/useCreatorToolOperations';
import { LocationInfoDisplay } from '../../location-info/location-info-display';
import { useAIPromptConfig } from '../hooks/use-ai-prompt-config';
import { useMapTextSuggestion } from '../hooks/use-map-text-suggestion';
import { DescriptionPreview } from './description-preview';
import { MapTextSuggestionPanel } from './map-text-suggestion-panel';
export interface LocationInfoDebugProps {
  selectedLocation: MapDataLocationFeature;
  category: MapDataCategory | null;
  subcategory: MapDataSubcategory | null;
  authToken: string | null;
  refreshAuthToken: (forceRefresh?: boolean) => Promise<string>;
  notifyUnauthorized: () => void;
  onClose: () => void;
}
export const LocationInfoDebug: React.FC<LocationInfoDebugProps> = ({
  selectedLocation,
  category,
  subcategory,
  authToken,
  refreshAuthToken,
  notifyUnauthorized,
  onClose
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<MapDataPinDescriptionTemplate | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [activeTextField, setActiveTextField] = useState<'title' | 'description' | null>(null);
  const [lightboxMedia, setLightboxMedia] = useState<{
    type: 'image' | 'video';
    src: string;
  } | null>(null);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const titleTextFieldRef = useRef<HTMLInputElement>(null);
  const descriptionTextFieldRef = useRef<HTMLDivElement>(null);
  const textSuggestionPanelRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const mapInfo = useAppSelector(selectCurrentMapInfo);
  const mapExternalId = useAppSelector(selectExternalMapId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const {
    data: debugMapData,
    refetch,
    isFetching,
    isError: isPinDescriptionTemplatesError
  } = useGetGameMapQuery({
    externalId: mapExternalId ?? '',
    debug: isInCreatorMode
  }, {
    skip: !mapExternalId
  });
  const pinDescriptionTemplates = debugMapData?.pinDescriptionTemplates ?? [];
  const hasPinDescriptionTemplates = pinDescriptionTemplates.length > 0;
  const isLoadingPinDescriptionTemplates = isFetching && !debugMapData;
  const pinDescriptionTemplatesEmptyMessage = isPinDescriptionTemplatesError ? 'Unable to load templates' : 'No templates configured';
  const [formState, setFormState] = useState<{
    title: string;
    description: string;
    selectedCategory: string;
    selectedSubcategory: string;
    verticalLevel: 'above' | 'below' | null;
  }>({
    title: selectedLocation.properties.title || '',
    description: selectedLocation.properties.description || '',
    selectedCategory: category?.externalId || '',
    selectedSubcategory: subcategory?.externalId || '',
    verticalLevel: selectedLocation.properties.verticalLevel ?? null
  });
  const {
    isUpdating,
    isDeleting,
    handleSaveMapLocation,
    handleDeleteMapLocation,
    startCloningLocation,
    startMovingLocation
  } = useCreatorToolOperations();
  const mapCategories = useAppSelector(selectMapCategories) || [];
  const mapSubcategories = useAppSelector(selectMapSubcategories) || [];
  const titlePromptConfig = useAIPromptConfig('title', mapInfo?.titleId);
  const descriptionPromptConfig = useAIPromptConfig('description', mapInfo?.titleId);
  const titleSuggestion = useMapTextSuggestion({
    field: 'title',
    initialValue: selectedLocation.properties.title || '',
    locationExternalId: selectedLocation.properties.externalId,
    mapExternalId,
    provider: titlePromptConfig.provider,
    model: titlePromptConfig.model,
    systemPrompt: titlePromptConfig.systemPrompt,
    maxTokens: titlePromptConfig.maxTokens,
    authToken,
    refreshAuthToken,
    notifyUnauthorized
  });
  const descriptionSuggestion = useMapTextSuggestion({
    field: 'description',
    initialValue: selectedLocation.properties.description || '',
    locationExternalId: selectedLocation.properties.externalId,
    mapExternalId,
    title: formState.title,
    provider: descriptionPromptConfig.provider,
    model: descriptionPromptConfig.model,
    systemPrompt: descriptionPromptConfig.systemPrompt,
    maxTokens: descriptionPromptConfig.maxTokens,
    authToken,
    refreshAuthToken,
    notifyUnauthorized
  });
  const saveFormStateBeforeUpload = useCallback(async (): Promise<boolean> => {
    try {
      await handleSaveMapLocation({
        ...selectedLocation,
        properties: {
          ...selectedLocation.properties,
          title: formState.title,
          description: formState.description,
          subcategoryExternalId: formState.selectedSubcategory,
          verticalLevel: formState.verticalLevel
        }
      });
      return true;
    } catch {
      toast({
        title: 'Failed to save changes',
        description: 'Please save your changes before uploading media.',
        variant: 'error'
      });
      return false;
    }
  }, [selectedLocation, formState, handleSaveMapLocation]);
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleImageUploadFromFile(file);
  };
  const extractVideoFrame = useCallback(async (video: HTMLVideoElement, videoUrl: string, file_0: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      video.src = videoUrl;
      video.muted = true;
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        video.currentTime = 0;
      };
      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('Failed to get video frame'));
            return;
          }
          const fileName = `${file_0.name.split('.')[0]}_frame.webp`;
          const frameFile = new File([blob], fileName, {
            type: 'image/webp'
          });
          resolve(frameFile);
        }, 'image/webp', 1);
      };
      video.onerror = () => reject(new Error('Failed to load video'));
      video.load();
    });
  }, []);

  // Utility function to handle clipboard data and convert to File objects
  const handleClipboardData = useCallback(async (clipboardData: DataTransfer): Promise<{
    imageFile?: File;
    videoFile?: File;
  }> => {
    const items = Array.from(clipboardData.items);
    let imageFile: File | undefined;
    let videoFile: File | undefined;
    for (const item of items) {
      if (item.kind === 'file') {
        const file_1 = item.getAsFile();
        if (file_1) {
          if (file_1.type.startsWith('image/')) {
            imageFile = file_1;
          } else if (file_1.type.startsWith('video/')) {
            videoFile = file_1;
          }
        }
      }
    }
    return {
      imageFile,
      videoFile
    };
  }, []);

  // Handle image upload from File object (used by both file input and clipboard)
  const handleImageUploadFromFile = useCallback(async (file_2: File) => {
    const saved = await saveFormStateBeforeUpload();
    if (!saved) return;
    setIsUploading(true);
    try {
      await updateMapPinMedia({
        externalPinId: selectedLocation.properties.externalId,
        image: file_2
      });
      await refetch();
      toast({
        title: 'Image uploaded successfully',
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: 'Failed to upload image',
        description: (error as Error).message,
        variant: 'error'
      });
    } finally {
      setIsUploading(false);
    }
  }, [selectedLocation.properties.externalId, refetch, saveFormStateBeforeUpload]);

  // Handle video upload from File object (used by both file input and clipboard)
  const handleVideoUploadFromFile = useCallback(async (file_3: File) => {
    const saved_0 = await saveFormStateBeforeUpload();
    if (!saved_0) return;
    setIsUploading(true);
    const video_0 = document.createElement('video');
    const videoUrl_0 = URL.createObjectURL(file_3);
    try {
      const imageFile_0 = await extractVideoFrame(video_0, videoUrl_0, file_3);
      await updateMapPinMedia({
        externalPinId: selectedLocation.properties.externalId,
        video: file_3,
        image: imageFile_0
      });
      await refetch();
      toast({
        title: 'Video uploaded successfully',
        variant: 'success'
      });
    } catch (error_0) {
      toast({
        title: 'Failed to upload video',
        description: (error_0 as Error).message,
        variant: 'error'
      });
    } finally {
      URL.revokeObjectURL(videoUrl_0);
      setIsUploading(false);
    }
  }, [selectedLocation.properties.externalId, refetch, extractVideoFrame, saveFormStateBeforeUpload]);
  const handleVideoUpload = async (e_0: React.ChangeEvent<HTMLInputElement>) => {
    const file_4 = e_0.target.files?.[0];
    if (!file_4) return;
    await handleVideoUploadFromFile(file_4);
  };

  // Handle clipboard paste events
  const handleClipboardPaste = useCallback(async (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    const {
      imageFile: imageFile_1,
      videoFile: videoFile_0
    } = await handleClipboardData(event.clipboardData);
    if (imageFile_1) {
      event.preventDefault();
      await handleImageUploadFromFile(imageFile_1);
    } else if (videoFile_0) {
      event.preventDefault();
      await handleVideoUploadFromFile(videoFile_0);
    }
  }, [handleImageUploadFromFile, handleVideoUploadFromFile, handleClipboardData]);
  const handleDeleteImage = async () => {
    setIsUploading(true);
    try {
      await handleSaveMapLocation({
        ...selectedLocation,
        properties: {
          ...selectedLocation.properties,
          imageId: null,
          videoId: null,
          imagePath: null,
          videoPath: null
        }
      });
      await refetch();
      toast({
        title: 'Image deleted successfully',
        variant: 'success'
      });
    } catch (error_1) {
      toast({
        title: 'Failed to delete image',
        description: (error_1 as Error).message,
        variant: 'error'
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleDeleteVideo = async () => {
    setIsUploading(true);
    try {
      await handleSaveMapLocation({
        ...selectedLocation,
        properties: {
          ...selectedLocation.properties,
          videoId: null,
          videoPath: null
        }
      });
      await refetch();
      toast({
        title: 'Video deleted successfully',
        variant: 'success'
      });
    } catch (error_2) {
      toast({
        title: 'Failed to delete video',
        description: (error_2 as Error).message,
        variant: 'error'
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleAddGameCoords = async () => {
    try {
      const coords = await getHost().getPlayerCoordinates();
      if (!coords || Number.isNaN(coords.x)) {
        toast({
          title: 'Could not get valid player coordinates',
          variant: 'error'
        });
        return;
      }

      // Update the selected location with game coordinates
      const updatedLocation = {
        ...selectedLocation,
        properties: {
          ...selectedLocation.properties,
          gameCoords: coords
        }
      };
      await handleSaveMapLocation(updatedLocation);
      toast({
        title: 'Game coordinates added successfully',
        variant: 'success'
      });
    } catch (error_3) {
      console.error('Failed to add game coordinates:', error_3);
      toast({
        title: 'Failed to add game coordinates',
        variant: 'error'
      });
    }
  };
  const handleRemoveGameCoords = async () => {
    try {
      await handleSaveMapLocation({
        ...selectedLocation,
        properties: {
          ...selectedLocation.properties,
          gameCoords: null
        }
      });
    } catch (error_4) {
      toast({
        title: 'Failed to remove game coordinates',
        description: JSON.stringify(error_4),
        variant: 'error'
      });
    }
  };
  const handleCopyLink = async () => {
    const protocolUrl = createWemodProtocolUrl({
      titleId: mapInfo?.titleId,
      mapId: mapInfo?.id,
      locationId: selectedLocation.properties.externalId
    });
    const result = await copyTextToClipboard(protocolUrl);
    if (result.success) {
      toast({
        title: 'Link copied to clipboard',
        variant: 'success'
      });
    } else {
      toast({
        title: 'Failed to copy link',
        variant: 'error'
      });
    }
  };
  const handleCopyId = async () => {
    const result_0 = await copyTextToClipboard(selectedLocation.properties.externalId);
    if (result_0.success) {
      toast({
        title: 'ID copied to clipboard',
        variant: 'success'
      });
    } else {
      toast({
        title: 'Failed to copy ID',
        variant: 'error'
      });
    }
  };
  const handleCloneLocation = () => {
    toast({
      title: 'Cloning Pin...',
      description: 'Select a location on the map.',
      variant: 'info'
    });
    startCloningLocation(selectedLocation);
  };
  const handleMoveLocation = () => {
    toast({
      title: 'Moving Pin...',
      description: 'Select a new location on the map.',
      variant: 'info'
    });
    startMovingLocation(selectedLocation);
  };
  const handleDeleteLocation = async () => {
    try {
      await handleDeleteMapLocation(selectedLocation);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error_5) {
      toast({
        title: 'Failed to delete location.',
        description: JSON.stringify(error_5),
        variant: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };
  const handleSave = async () => {
    try {
      await handleSaveMapLocation({
        ...selectedLocation,
        properties: {
          ...selectedLocation.properties,
          title: formState.title,
          description: formState.description,
          subcategoryExternalId: formState.selectedSubcategory,
          verticalLevel: formState.verticalLevel
        }
      });
      dispatch(setLastSelectedCategory(formState.selectedCategory));
      dispatch(setLastSelectedSubcategory(formState.selectedSubcategory));

      // Keep the panel open and show success toast. Data will refresh via RTK invalidation.
      toast({
        title: 'Location saved',
        variant: 'success'
      });
    } catch (error_6) {
      toast({
        title: 'Failed to save location.',
        description: JSON.stringify(error_6),
        variant: 'error'
      });
    }
  };
  const isTemplateContent = (content: string) => {
    return pinDescriptionTemplates.some(t => t.content.trim() === content.trim());
  };
  const applyTemplate = (template: MapDataPinDescriptionTemplate) => {
    setFormState({
      ...formState,
      description: template.content
    });
    descriptionSuggestion.handleUserInput(template.content);
    setPendingTemplate(null);
    setShowTemplateConfirm(false);
  };
  const handleSelectTemplate = (template_0: MapDataPinDescriptionTemplate) => {
    const description = formState.description;
    if (description.trim() === '' || isTemplateContent(description)) {
      applyTemplate(template_0);
    } else {
      setPendingTemplate(template_0);
      setShowTemplateConfirm(true);
    }
  };

  // Filter subcategories for selected category
  const filteredSubcategories = mapSubcategories.filter((sub: MapDataSubcategory) => sub.categoryExternalId === formState.selectedCategory);

  // Set up clipboard paste event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handlePaste = (event_0: ClipboardEvent) => {
      void handleClipboardPaste(event_0);
    };
    container.addEventListener('paste', handlePaste);
    return () => {
      container.removeEventListener('paste', handlePaste);
    };
  }, [handleClipboardPaste]);
  if (previewMode) {
    return <div style={{
      position: 'relative'
    }}>
                <FlexColumn gap="12" className={css.raw({
        width: '[340px]',
        background: 'wemod.elevated.100',
        borderRadius: '24',
        overflow: 'hidden',
        boxShadow: 'lg',
        zIndex: '[2]',
        padding: '12'
      })} style={{
        position: 'relative',
        zIndex: 4,
        maxHeight: 'min(500px, calc(100vh - 56px - 16px))'
      }}>
                    <Button onClick={() => setPreviewMode(false)} color="warning" size="sm" fullWidth>
                        Exit Preview
                    </Button>

                    <LocationInfoDisplay onPanToLocation={() => {}} title={formState.title} description={formState.description} imageId={selectedLocation.properties.imageId} videoPath={selectedLocation.properties.videoPath} categoryColor={category?.color || DEFAULT_PIN_COLOR} subcategoryIcon={subcategory?.icon || ''} subcategoryTitle={subcategory?.title} gameCoords={selectedLocation.properties.gameCoords ?? undefined} onClose={onClose} />
                </FlexColumn>
            </div>;
  }
  const showDescriptionPreview = activeTextField === 'description' && formState.description;
  const showTitleSuggestion = activeTextField === 'title' && titleSuggestion.isVisible;
  const showDescriptionSuggestion = activeTextField === 'description' && descriptionSuggestion.isVisible;
  const showExternalSuggestion = showTitleSuggestion || showDescriptionSuggestion;
  const handleTextFieldBlur = () => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (activeElement && (titleTextFieldRef.current?.contains(activeElement) || descriptionTextFieldRef.current?.contains(activeElement) || textSuggestionPanelRef.current?.contains(activeElement))) {
        return;
      }
      setActiveTextField(null);
    });
  };
  return <div className={css({
    position: 'relative'
  })}>
            {(showDescriptionPreview || showExternalSuggestion) &&
    // biome-ignore lint/a11y/noStaticElementInteractions: this wrapper only tracks focus containment for the external suggestion panel
    <div ref={textSuggestionPanelRef} onBlur={handleTextFieldBlur} className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: '12',
      position: 'absolute',
      right: '[calc(100% + 12px)]',
      top: 'none',
      width: '[340px]',
      zIndex: '[2]'
    })}>
                    {showDescriptionPreview && <DescriptionPreview description={formState.description} />}
                    {showTitleSuggestion && <MapTextSuggestionPanel className={css.raw({
        marginTop: '0',
        backgroundColor: 'wemod.elevated.100',
        width: 'full'
      })} label="Suggested title" phase={titleSuggestion.phase} suggestion={titleSuggestion.suggestion} currentValue={formState.title} error={titleSuggestion.error} onAccept={() => {
        setFormState(current => ({
          ...current,
          title: titleSuggestion.suggestion
        }));
        titleSuggestion.acceptSuggestion();
      }} onDismiss={titleSuggestion.dismissSuggestion} onRetry={titleSuggestion.retrySuggestion} />}
                    {showDescriptionSuggestion && <MapTextSuggestionPanel className={css.raw({
        marginTop: '0',
        backgroundColor: 'wemod.elevated.100',
        width: 'full'
      })} label="Suggested description" phase={descriptionSuggestion.phase} suggestion={descriptionSuggestion.suggestion} currentValue={formState.description} error={descriptionSuggestion.error} onAccept={() => {
        setFormState(current_0 => ({
          ...current_0,
          description: descriptionSuggestion.suggestion
        }));
        descriptionSuggestion.acceptSuggestion();
      }} onDismiss={descriptionSuggestion.dismissSuggestion} onRetry={descriptionSuggestion.retrySuggestion} />}
                </div>}
            <div ref={containerRef}
    // biome-ignore lint/a11y/noNoninteractiveTabindex: container is interactive
    tabIndex={0} style={{
      outline: 'none'
    }} className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: '12',
      width: 'full',
      maxWidth: '[400px]',
      background: 'wemod.elevated.100',
      borderRadius: '24',
      p: '12',
      px: '16',
      boxShadow: 'lg',
      zIndex: 2,
      overflow: 'hidden'
    })}>
                {/* Header with dropdown menu */}
                <FlexRow align="center" gap="8" width="full">
                    <Type variant="header-s" color="white">
                        Edit Location
                    </Type>
                    <DropdownMenu trigger={<IconButton color="secondary" size="sm" aria-label="More options" className={css({
          marginLeft: 'auto'
        })}>
                                <Icon icon="more_vert" decorative />
                            </IconButton>}>
                        <DropdownMenuItem value="copy-link" onClick={handleCopyLink}>
                            Copy Link
                        </DropdownMenuItem>
                        <DropdownMenuItem value="copy-id" onClick={handleCopyId}>
                            Copy ID ({selectedLocation.properties.externalId})
                        </DropdownMenuItem>
                        <DropdownMenuItem value="clone" onClick={handleCloneLocation}>
                            Clone Pin
                        </DropdownMenuItem>
                        <DropdownMenuItem value="move" onClick={handleMoveLocation}>
                            Move Pin
                        </DropdownMenuItem>
                        <DropdownMenuItem value="delete-location" destructive onClick={() => setShowDeleteConfirm(true)} disabled={isDeleting}>
                            Delete Location
                        </DropdownMenuItem>
                    </DropdownMenu>

                    <IconButton onClick={onClose} color="secondary" size="sm" aria-label="Close">
                        <Icon icon="close" decorative />
                    </IconButton>
                </FlexRow>

                <ScrollableContent maxHeight="500px">
                    <FlexColumn gap="8" width="full">
                        {/* Content */}
                        <FlexColumn gap="8" width="full" className={css.raw({
            background: 'whiteAlpha.5',
            borderRadius: '12',
            p: '12'
          })}>
                            <TextInput ref={titleTextFieldRef} placeholder="Location Title" label="Title" value={formState.title} onFocus={() => setActiveTextField('title')} onBlur={handleTextFieldBlur} onChange={e_1 => {
              const nextTitle = e_1.target.value;
              setFormState({
                ...formState,
                title: nextTitle
              });
              titleSuggestion.handleUserInput(nextTitle);
            }} fullWidth size="sm" />

                            {/* biome-ignore lint/a11y/noStaticElementInteractions: this wrapper only tracks focus containment for the markdown editor */}
                            <div ref={descriptionTextFieldRef} className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '4',
              width: 'full'
            })} onFocus={() => setActiveTextField('description')} onBlur={handleTextFieldBlur}>
                                <FlexRow align="center" gap="8" width="full">
                                    <Type variant="inputHint">Description:</Type>
                                    <DropdownMenu trigger={<Button color="secondary" size="sm" disabled={isLoadingPinDescriptionTemplates} className={css({
                  marginLeft: 'auto'
                })}>
                                                <Icon icon="description" decorative />
                                                {isLoadingPinDescriptionTemplates ? 'Loading templates...' : 'Templates'}
                                            </Button>}>
                                        {hasPinDescriptionTemplates ? pinDescriptionTemplates.map(template_1 => <DropdownMenuItem key={template_1.name} value={template_1.name} onClick={() => handleSelectTemplate(template_1)}>
                                                    {template_1.name}
                                                </DropdownMenuItem>) : <DropdownMenuItem value="empty" disabled>
                                                {pinDescriptionTemplatesEmptyMessage}
                                            </DropdownMenuItem>}
                                    </DropdownMenu>
                                </FlexRow>
                                <MarkdownEditor value={formState.description} onChange={e_2 => {
                setFormState({
                  ...formState,
                  description: e_2
                });
                descriptionSuggestion.handleUserInput(e_2);
              }} maxLength={2000} className={css({
                maxWidth: '[500px]'
              })} fullWidth />
                            </div>
                        </FlexColumn>

                        {/* Media */}
                        <FlexColumn gap="8" width="full" className={css.raw({
            background: 'whiteAlpha.5',
            borderRadius: '12',
            p: '12'
          })}>
                            <FlexRow gap="8" width="full">
                                {/* Image Upload */}
                                <FlexColumn width="full" gap="4" className={css.raw({
                minWidth: '[0]'
              })}>
                                    {selectedLocation.properties.imageId ? <button type="button" onClick={() => setLightboxMedia({
                  type: 'image',
                  src: `${process.env.NEXT_PUBLIC_CDN_URL}/game_map_pin/${selectedLocation.properties.imageId}/750.webp`
                })} className={css({
                  width: 'full',
                  cursor: 'pointer',
                  borderWidth: 0,
                  p: 'none',
                  background: 'transparent'
                })}>
                                            <img src={`${process.env.NEXT_PUBLIC_CDN_URL}/game_map_pin/${selectedLocation.properties.imageId}/750.webp`} alt="Location" className={css({
                    width: 'full',
                    height: '[80px]',
                    borderRadius: '4',
                    objectFit: 'cover',
                    display: 'block'
                  })} />
                                        </button> : <div className={css({
                  width: 'full',
                  height: '[80px]',
                  borderRadius: '4',
                  background: 'whiteAlpha.5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                })}>
                                            <Icon icon="hide_image" size="md" className={css({
                    color: 'white'
                  })} decorative />
                                        </div>}
                                    <Button onClick={() => imageInputRef.current?.click()} color="secondary" size="sm" radius="rounded" fullWidth disabled={isUploading || isFetching}>
                                        <Icon icon={isUploading ? 'hourglass_empty' : 'image'} decorative />
                                        {isUploading ? 'Uploading...' : selectedLocation.properties.imagePath ? 'Replace Image' : 'Upload Image'}
                                    </Button>
                                    <input ref={imageInputRef} type="file" accept="image/*" style={{
                  display: 'none'
                }} onChange={handleImageUpload} disabled={isUploading} />
                                    <Button onClick={handleDeleteImage} color="error" size="sm" radius="rounded" fullWidth disabled={isUploading || !selectedLocation.properties.imageId}>
                                        <Icon icon="delete" decorative />
                                        Delete Image
                                    </Button>
                                </FlexColumn>

                                {/* Video Upload */}
                                <FlexColumn width="full" gap="4" className={css.raw({
                minWidth: '[0]'
              })}>
                                    {selectedLocation.properties.videoPath && selectedLocation.properties.videoId ? <button type="button" onClick={() => setLightboxMedia({
                  type: 'video',
                  src: selectedLocation.properties.videoPath ?? ''
                })} className={css({
                  width: 'full',
                  height: '[80px]',
                  borderRadius: '4',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative',
                  background: 'blackAlpha.40',
                  borderWidth: 0,
                  p: 'none'
                })}>
                                            {/* biome-ignore lint/a11y/useMediaCaption: video thumbnail */}
                                            <video src={selectedLocation.properties.videoPath} className={css({
                    width: 'full',
                    height: 'full',
                    objectFit: 'cover'
                  })} />
                                        </button> : <div className={css({
                  width: 'full',
                  height: '[80px]',
                  borderRadius: '4',
                  background: 'whiteAlpha.5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                })}>
                                            <Icon icon="videocam_off" size="md" className={css({
                    color: 'white'
                  })} decorative />
                                        </div>}
                                    <Button onClick={() => videoInputRef.current?.click()} color="secondary" size="sm" radius="rounded" fullWidth disabled={isUploading || isFetching}>
                                        <Icon icon={isUploading ? 'hourglass_empty' : 'video_library'} decorative />
                                        {isUploading ? 'Uploading...' : selectedLocation.properties.videoPath && selectedLocation.properties.videoId ? 'Replace Video' : 'Upload Video'}
                                    </Button>
                                    <input ref={videoInputRef} type="file" accept="video/*" style={{
                  display: 'none'
                }} onChange={handleVideoUpload} disabled={isUploading} />
                                    <Button onClick={handleDeleteVideo} color="error" size="sm" radius="rounded" fullWidth disabled={isUploading || !selectedLocation.properties.videoPath}>
                                        <Icon icon="delete" decorative />
                                        Delete Video
                                    </Button>
                                </FlexColumn>
                            </FlexRow>
                        </FlexColumn>

                        {/* Classification */}
                        <FlexColumn gap="8" width="full" className={css.raw({
            background: 'whiteAlpha.5',
            borderRadius: '12',
            p: '12'
          })}>
                            <Select label="Category:" fullWidth value={formState.selectedCategory} onValueChange={value => {
              setFormState({
                ...formState,
                selectedCategory: value
              });
              dispatch(setLastSelectedCategory(value));
            }} trigger={<Button color="secondary" size="sm" radius="rounded" fullWidth>
                                        {mapCategories.find((cat: MapDataCategory) => cat.externalId === formState.selectedCategory)?.title || 'Select Category'}
                                    </Button>} placeholder="Select Category">
                                {mapCategories.map((cat_0: MapDataCategory) => <SelectItem key={cat_0.externalId} item={{
                label: cat_0.title,
                value: cat_0.externalId
              }}>
                                        {cat_0.title}
                                    </SelectItem>)}
                            </Select>
                            <Select label="Subcategory:" fullWidth value={formState.selectedSubcategory} onValueChange={value_0 => {
              setFormState({
                ...formState,
                selectedSubcategory: value_0
              });
              dispatch(setLastSelectedSubcategory(value_0));
            }} trigger={<Button color="secondary" size="sm" radius="rounded" fullWidth>
                                        {filteredSubcategories.find((sub_0: MapDataSubcategory) => sub_0.externalId === formState.selectedSubcategory)?.title || 'Select Subcategory'}
                                    </Button>} placeholder="Select Subcategory">
                                {filteredSubcategories.map((sub_1: MapDataSubcategory) => <SelectItem key={sub_1.externalId} item={{
                label: sub_1.title,
                value: sub_1.externalId
              }}>
                                        {sub_1.title}
                                    </SelectItem>)}
                            </Select>
                            <Select label="Vertical Level Indicator:" fullWidth value={formState.verticalLevel ?? 'none'} onValueChange={value_1 => {
              setFormState({
                ...formState,
                verticalLevel: value_1 === 'none' ? null : value_1 as 'above' | 'below'
              });
            }} trigger={<Button color="secondary" size="sm" radius="rounded" fullWidth>
                                        {formState.verticalLevel === 'above' ? 'Above' : formState.verticalLevel === 'below' ? 'Below' : 'None'}
                                    </Button>} placeholder="Select Vertical Level">
                                <SelectItem item={{
                label: 'None',
                value: 'none'
              }}>None</SelectItem>
                                <SelectItem item={{
                label: 'Above',
                value: 'above'
              }}>Above</SelectItem>
                                <SelectItem item={{
                label: 'Below',
                value: 'below'
              }}>Below</SelectItem>
                            </Select>
                        </FlexColumn>

                        {/* Position */}
                        <FlexColumn width="full" gap="8" className={css.raw({
            background: 'whiteAlpha.5',
            borderRadius: '12',
            p: '12'
          })}>
                            <Button onClick={handleAddGameCoords} color="secondary" size="sm" radius="rounded" fullWidth>
                                <Icon icon="add_location_alt" decorative />
                                {selectedLocation.properties.gameCoords ? 'Update Game Coordinates' : 'Add Game Coordinates'}
                            </Button>
                            {selectedLocation.properties.gameCoords && <Button onClick={handleRemoveGameCoords} color="error" size="sm" radius="rounded" fullWidth>
                                    <Icon icon="delete" decorative />
                                    Remove Game Coordinates
                                </Button>}
                            {selectedLocation.properties.gameCoords && <Type variant="body-xs" color="cyan.600">
                                    X: {selectedLocation.properties.gameCoords?.x}, Y:{' '}
                                    {selectedLocation.properties.gameCoords?.y}
                                </Type>}
                        </FlexColumn>
                    </FlexColumn>
                </ScrollableContent>

                <FlexColumn gap="8" justify="end" width="full">
                    <Button onClick={handleSave} color="success" size="sm" fullWidth disabled={isUpdating}>
                        {isUpdating ? 'Saving...' : 'Save'}
                    </Button>
                </FlexColumn>

                <Modal size="sm" isOpen={showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(false)}>
                    <FlexColumn gap="12" align="center">
                        <Type variant="header-s">Delete this location?</Type>
                        <Type variant="caption" color="red.400">
                            This action cannot be undone.
                        </Type>
                        <FlexRow gap="8">
                            <Button onClick={() => setShowDeleteConfirm(false)} color="secondary">
                                Cancel
                            </Button>
                            <Button onClick={handleDeleteLocation} color="error" disabled={isDeleting}>
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </FlexRow>
                    </FlexColumn>
                </Modal>

                <Modal size="sm" isOpen={showTemplateConfirm} onOpenChange={() => {
        setShowTemplateConfirm(false);
        setPendingTemplate(null);
      }}>
                    <FlexColumn gap="12" align="center">
                        <Type variant="header-s">Replace description?</Type>
                        <Type variant="caption" color="whiteAlpha.60">
                            The current description will be replaced with the "{pendingTemplate?.name}" template.
                        </Type>
                        <FlexRow gap="8">
                            <Button onClick={() => {
              setShowTemplateConfirm(false);
              setPendingTemplate(null);
            }} color="secondary">
                                Cancel
                            </Button>
                            <Button onClick={() => {
              if (pendingTemplate) applyTemplate(pendingTemplate);
            }} color="warning">
                                Replace
                            </Button>
                        </FlexRow>
                    </FlexColumn>
                </Modal>

                <Modal size="lg" isOpen={!!lightboxMedia} onOpenChange={() => setLightboxMedia(null)}>
                    <FlexColumn gap="8" align="center">
                        {lightboxMedia?.type === 'image' && <img src={lightboxMedia.src} alt="Location" className={css({
            width: 'full',
            borderRadius: '6'
          })} />}
                        {lightboxMedia?.type === 'video' &&
          // biome-ignore lint/a11y/useMediaCaption: lightbox video preview
          <video src={lightboxMedia.src} controls playsInline className={css({
            width: 'full',
            borderRadius: '6'
          })} />}
                    </FlexColumn>
                </Modal>
            </div>
        </div>;
};